import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type { EmergencyFundSection, FundGoalKey, FundOption } from './types';

/** Legacy capped `Current amount saved` alone at 999,999 while its own error copy said 9,999,999; the copy wins. */
export const MAX_FIELD_VALUE = 9999999;

export const RANGE_ERROR = 'Enter a number between 0 and 9,999,999';

/** Verbatim from the legacy page; it is longer than the Budget Calculator's. */
export const DISCLAIMER =
  'This calculator is for educational purposes only. It makes estimates based on the information you provide, and your ideal emergency fund amount may change if any of the information varies. You should enter figures that are appropriate to your individual situation. This calculator is not intended to offer any tax, legal, financial or investment advice.';

export const FLAT_GOAL_AMOUNT = 1000;

export const EXPENSE_FIELDS: readonly CalculatorField[] = [
  { label: 'Rent or mortgage payment', hint: 'Enter your monthly rent or mortgage payment.' },
  {
    label: 'Debt payments',
    hint: 'Enter how much you pay on credit cards and all loans (except your mortgage and car payment) every month.',
  },
  {
    label: 'Utility payments',
    hint: 'Enter the combined amount you pay for gas, water, electricity, garbage, or other utilities each month.',
  },
  {
    label: 'Phone & internet payments',
    hint: 'Enter the combined monthly total of your phone, internet, cable, streaming services, and any other similar payments.',
  },
  {
    label: 'Insurance payments',
    hint: 'Enter the combined monthly total of car, health, life, and home insurance payments as well as other insurance payments that aren’t deducted from your paycheck by your employer.',
  },
  {
    label: 'Transportation expenses',
    hint: 'Enter the combined monthly total of your car payment, fuel, and public transportation costs.',
  },
  { label: 'Grocery expenses', hint: 'Enter the combined monthly amount you spend on groceries.' },
  {
    label: 'Other expenses',
    hint: 'Enter the combined monthly total of any other expenses. Include the cost of prescription medications, gym memberships, clothes, etc.',
  },
];

export const MONTHLY_CONTRIBUTION_FIELD: CalculatorField = {
  label: 'Amount you can save each month',
  hint: 'Enter the amount of money you can comfortably save each month.',
};

export const CURRENT_SAVINGS_FIELD: CalculatorField = {
  label: 'Current amount saved',
  hint: 'Enter the total amount of money you’ve already saved for your emergency fund.',
};

/** Only the expense section feeds the monthly total the goal is a multiple of. */
export const EMERGENCY_FUND_SECTIONS: readonly EmergencyFundSection[] = [
  { key: 'expenses', legend: 'Monthly expenses', fields: EXPENSE_FIELDS },
  { key: 'savings', legend: 'Your savings', fields: [MONTHLY_CONTRIBUTION_FIELD, CURRENT_SAVINGS_FIELD] },
];

export const EMERGENCY_FUND_FIELDS: readonly CalculatorField[] = EMERGENCY_FUND_SECTIONS.flatMap(
  section => section.fields,
);

/** Start empty so both calculators share the same input behavior. */
export const INITIAL_VALUES: CalculatorFieldValues = Object.fromEntries(
  EMERGENCY_FUND_FIELDS.map(field => [field.label, '']),
);

export const FUND_OPTIONS: readonly FundOption[] = [
  { key: '3', label: '3-month fund' },
  { key: '4', label: '4-month fund' },
  { key: '5', label: '5-month fund' },
  { key: '6', label: '6-month fund' },
  { key: '7', label: '7-month fund' },
  { key: '8', label: '8-month fund' },
  { key: '9', label: '9-month fund' },
  { key: 'flat-1000', label: '$1,000 fund' },
];

/** Legacy re-selected the first option on every calculation rather than keeping the last choice. */
export const DEFAULT_GOAL_KEY: FundGoalKey = '3';

/** Legacy's tooltip; the label alone reads as the sentence the picker completes. */
export const DURATION_FIELD: CalculatorField = {
  label: 'To create a',
  hint: 'Select the number of months to save depending on your lifestyle or family size: Single people should try to save at least three months of expenses, and families should try to save for nine.',
};

export const RESULT_LABELS = {
  monthlyExpenses: 'Monthly expenses total:',
  monthlyContribution: 'If you continue to save',
  goal: 'You’ll need to save',
  monthsToGoal: 'Saving the full amount will take',
} as const;

/** Legacy showed this wherever a zero contribution left the timeline undefined. */
export const NO_TIMELINE = '-';
